/**
 * fmContextPanelInternals — non-component exports for FmContextPanelContext.
 *
 * The context object and the `useFmContextPanel` hook live here (not in the
 * .tsx) so the .tsx file only exports React components. That keeps
 * react-refresh fast-refresh working and avoids the
 * `react-refresh/only-export-components` lint warning on the component file.
 */
import { createContext, useContext } from "react";
import type React from "react";

/** A single panel entry registered by a page view. */
export interface PanelEntry {
  /** Unique key identifying this panel within the current view */
  key: string;
  /** Icon rendered in the icon-rail (fixed width) */
  icon: React.ReactNode;
  /** Tooltip text naming the panel — REQUIRED for accessibility (E1 spec) */
  tooltip: string;
  /** Panel body content. null/undefined → panel shows empty state for this panel */
  content: React.ReactNode;
  /** False keeps semantic/action-only panels out of the compact map toolbar. */
  visibleInMapTools?: boolean;
}

export interface FmContextPanelContextValue {
  panels: PanelEntry[];
  setPanels: React.Dispatch<React.SetStateAction<PanelEntry[]>>;
  mapToolsPlacement: FmMapToolsPlacement;
  setMapToolsPlacement: React.Dispatch<
    React.SetStateAction<FmMapToolsPlacement>
  >;
  activePanel: string | null;
  isPanelOpen: boolean;
  isPanelPinned: boolean;
  openPanel: (key: string) => void;
  peekPanel: (key: string) => void;
  scheduleClosePeekedPanel: () => void;
  cancelPeekClose: () => void;
  closePanel: () => void;
  /** Toggle: same key+open → close; else open with key */
  togglePanel: (key: string) => void;
}

export type FmMapToolsPlacement = "shell" | "local";

export const FmContextPanelContext =
  createContext<FmContextPanelContextValue | null>(null);

/** Hook to access panel context. Must be inside FmContextPanelProvider. */
export function useFmContextPanel(): FmContextPanelContextValue {
  const ctx = useContext(FmContextPanelContext);
  if (!ctx) {
    throw new Error(
      "useFmContextPanel must be used inside FmContextPanelProvider",
    );
  }
  return ctx;
}
